import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ArrowLeft, Mail, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { DevEmailDTO } from '@regent/shared';
import { Button, EmptyState, SkeletonRows } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/dev/emails')({ component: DevEmailsPage });

const KEY = ['dev', 'emails'];

/** Outbox for the development email fallback (used when RESEND_API_KEY is not set). */
function DevEmailsPage() {
  const qc = useQueryClient();
  const emails = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<{ emails: DevEmailDTO[] }>('/dev/emails')).emails,
    refetchInterval: 4000,
    retry: false,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const list = emails.data ?? [];
  const selected = list.find((e) => e.id === selectedId) ?? list[0];

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
        <Button asChild size="icon-sm" variant="ghost" aria-label="Back to app">
          <Link to="/">
            <ArrowLeft />
          </Link>
        </Button>
        <Mail className="size-4 text-fg-muted" />
        <h1 className="text-sm font-semibold">Dev emails</h1>
        <span className="text-xs text-fg-subtle">
          Emails land here when no email provider key is configured.
        </span>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => void emails.refetch()}>
          <RefreshCw /> Refresh
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={list.length === 0}
          onClick={async () => {
            await api.delete('/dev/emails');
            await qc.invalidateQueries({ queryKey: KEY });
          }}
        >
          <Trash2 /> Clear
        </Button>
      </header>
      {emails.isLoading ? (
        <SkeletonRows className="p-4" />
      ) : emails.isError ? (
        <EmptyState
          className="flex-1"
          title="Not available"
          description="The dev email viewer only exists in development."
        />
      ) : list.length === 0 ? (
        <EmptyState
          className="flex-1"
          icon={<Mail />}
          title="No emails yet"
          description="Invite someone or request a password reset and the email will show up here."
        />
      ) : (
        <div className="flex min-h-0 flex-1">
          <ul className="w-80 shrink-0 overflow-y-auto border-r border-border" aria-label="Emails">
            {list.map((email) => (
              <li key={email.id}>
                <button
                  onClick={() => setSelectedId(email.id)}
                  aria-current={selected?.id === email.id}
                  className={cn(
                    'block w-full border-b border-border px-3 py-2.5 text-left hover:bg-hover',
                    selected?.id === email.id && 'bg-active hover:bg-active',
                  )}
                >
                  <p className="truncate text-sm font-medium">{email.subject}</p>
                  <p className="flex justify-between gap-2 text-xs text-fg-muted">
                    <span className="truncate">To {email.to}</span>
                    <span className="shrink-0">{format(email.createdAt, 'MMM d, h:mm a')}</span>
                  </p>
                </button>
              </li>
            ))}
          </ul>
          {selected ? (
            <article className="flex min-w-0 flex-1 flex-col">
              <div className="border-b border-border px-5 py-3">
                <h2 className="font-semibold">{selected.subject}</h2>
                <p className="text-sm text-fg-muted">
                  To <span data-testid="email-to">{selected.to}</span>
                </p>
              </div>
              {/* Sandboxed: the email HTML cannot run scripts or touch the app. Links open in a new tab. */}
              <iframe
                title="Email body"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                srcDoc={`<base target="_blank">${selected.html}`}
                className="min-h-0 flex-1 bg-white"
              />
              <details className="border-t border-border px-5 py-2 text-sm">
                <summary className="cursor-pointer text-fg-muted">Plain text</summary>
                <pre className="mt-2 font-mono text-xs whitespace-pre-wrap" data-testid="email-text">
                  {selected.text}
                </pre>
              </details>
            </article>
          ) : null}
        </div>
      )}
    </div>
  );
}
